import { Constants } from "utils";

/**
 * function to check if the given value is an object
 * @param {any} value object to be tested
 * @returns {boolean} true if the value is an object but not an array
 */

export const isObject = (value: any): boolean => !!value && Object.prototype.toString.call(value) === "[object Object]";

/**
 * function to check if the given value is a number
 * @param {any} value number to be tested
 * @returns {boolean} true if the value is a number
 */

export const isNumber = (value?: string | number): boolean => {
  return value != null && value !== "" && !isNaN(Number(value.toString()));
};

/**
 * function to interpolate a string with their respective values
 * @param {string} str string e.g. the {{user}} must be logged in
 * @param {any} obj object e.g. { user: 'username' }
 * @return {string} the username must be logged in
 */

export const interpolate = (str: string, obj: any): string => {
  return Object.keys(obj).reduce((previousValue: string, currentValue: string) => {
    return previousValue.replaceAll(`{{${currentValue}}}`, obj[currentValue]);
  }, str);
};

/**
 * function to interpolate a URL with their respective values
 * @param {string} url string e.g. /foo/bar/:id
 * @param {any} obj object e.g. { id: 12345 }
 * @return {string} /foo/bar/12345
 */

export const interpolateURL = (url: string, obj: any): string => {
  return Object.keys(obj).reduce((previousValue: string, currentValue: string) => {
    return previousValue.replaceAll(`:${currentValue}`, obj[currentValue]);
  }, url);
};

export const interpolatePhone = (phone: string) => {
  let i = 0;
  return Constants.INPUT_MASK.PHONE.replace(/9/g, () => phone.charAt(i++));
};

/**
 * function to convert a given obj to query string
 * @param {string} url  string e.g. /foo/bar
 * @param {any} obj the object to be converted e.g. { id: 123, name: "xyz" }
 * @returns {string} queryString e.g. "/foo/bar?id=123&name=xyz"
 */

export const toQueryString = (url: string, obj: any): string => {
  return (
    url +
    "?" +
    Object.keys(obj)
      .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]))
      .join("&")
  );
};

/**
 * function to return the respective greeting to the user according to the current time
 * @return {string} the greeting message
 */

export const greeting = (): string => {
  const hour = new Date().getHours();
  return hour < 12 ? "good_morning" : hour > 12 && hour < 18 ? "good_afternoon" : hour > 22 ? "hello" : "good_evening";
};

/**
 * function to remove time from date
 * @param {string} date the date to be updated
 * @return {Date} the date without time
 */

export const clearTime = (date: string | Date | number): Date => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

/**
 * function to calculate the days between two dates
 * @param {string | Date} a the date to be compared
 * @param {string | Date} b the date to be compared
 * @return {number} number of the days
 */

export const inBetween = (a: string | Date, b: string | Date): number => {
  const inBetween = new Date(b).getTime() - new Date(a).getTime();
  return Math.round(inBetween / (1000 * 60 * 60 * 24));
};

/**
 * function to compare two given dates
 * @param {string | Date} a the date to be compared
 * @param {string | Date} b the date to be compared
 * @return {boolean} if the dates are the same
 */

export const isDateEquals = (a: string | Date, b: string | Date): boolean => {
  return clearTime(a).getTime() === clearTime(b).getTime();
};

/**
 * function to return a friendly date
 * @return {string} the friendly date message
 */

export const toRelativeDate = (str: string | number, t: any, locale: string = "en", dateOnly: boolean = false): string => {
  const date = new Date(str);
  const today = clearTime(new Date());
  const days = inBetween(date, today);
  const dateWithoutTime = clearTime(date);
  const tomorrow = clearTime(new Date(today).setDate(today.getDate() + 1));
  const yesterday = clearTime(new Date(today).setDate(today.getDate() - 1));

  return isDateEquals(dateWithoutTime, today)
    ? dateOnly
      ? t.today
      : interpolate(t.today_at, { time: date.toLocaleTimeString(locale, { ...Constants.TIME_FORMAT }) })
    : isDateEquals(dateWithoutTime, tomorrow)
    ? dateOnly
      ? t.tomorrow
      : interpolate(t.tomorrow_at, { time: date.toLocaleTimeString(locale, { ...Constants.TIME_FORMAT }) })
    : isDateEquals(dateWithoutTime, yesterday)
    ? dateOnly
      ? t.yesterday
      : interpolate(t.yesterday_at, { time: date.toLocaleTimeString(locale, { ...Constants.TIME_FORMAT }) })
    : Math.abs(days) <= 3
    ? days > 0
      ? interpolate(t.day_ago, { day: Math.abs(days) })
      : interpolate(t.in_day, { day: Math.abs(days) })
    : dateOnly
    ? date.toLocaleDateString(locale, { ...Constants.DATE_FORMAT })
    : date.toLocaleString(locale, { ...Constants.DATE_FORMAT, ...Constants.TIME_FORMAT });
};

/**
 * function to join classNames
 * @param {any} args classes
 * @return {string} merged array of classes
 */

export const classNames = (...args: any): string => {
  return args.filter((x: any) => !!x).join(" ");
};

/**
 * function to change a position in the array (for state)
 * @param {any[]} arr array to be modified
 * @param {number} index index to be updated
 * @param {any} value value to be updated
 * @return {any[]} merged array of classes
 */

export const setArrayIndex = (arr: any[], index: number, value: any) => {
  const items = [...arr];
  items[index] = value;
  return items;
};

/**
 * function to check if the given dates are equal
 * @param {date} a date
 * @param {date} b date
 * @returns {boolean} true if the dates are equal
 */

export const compareTo = (a: Date, b: Date): boolean => {
  return !a || !b ? false : a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
};

/**
 * function to check if the assigned date is within the given range
 * @param {date} value date to be tested
 * @param {date} min min date
 * @param {date} max max date
 * @returns {boolean} true if the date is within the range
 */

export const inRange = (value: Date, min?: Date, max?: Date): boolean => {
  if (!value) return false;
  return min && max
    ? (value > min || compareTo(value, min)) && (value < max || compareTo(value, max))
    : min && !max
    ? value > min || compareTo(value, min)
    : !min && max
    ? value < max || compareTo(value, max)
    : true;
};

/**
 * function to check if a given string has any value
 * @param {string[]} params values to be tested
 * @returns {boolean} true if it has value, false otherwise
 */

export const hasValue = (...params: (string | undefined | null)[]): boolean => {
  for (const value of params) {
    if (!!!(value !== null && value !== undefined && value.toString().trim().length > 0)) {
      return false;
    }
  }
  return true;
};

/**
 * function to check if the given password meets all the rules
 * @param {string} password given password
 * @return {boolean} if the password meets all the rules
 */

export const validatePasswordPolicy = (password: string): boolean => {
  if (!hasValue(password)) return false;
  if (password.length < 8) return false;
  if (!/[0-9]/.test(password)) return false;
  if (!/[a-z]/gi.test(password)) return false;
  if (!/[A-Z]/gi.test(password)) return false;
  if (!/[@#\\$%&*]/gi.test(password)) return false;
  return true;
};

/**
 * function to convert seconds to days, hours, minutes, seconds
 * @param {number} value time to be converted (in seconds)
 * @returns {any} an object containing the count of days, hours, minutes and seconds or the value itself
 */

export const time = (value: number): any => {
  if (value <= 0) return value;
  else {
    const day = Math.floor(value / 86400);
    const hour = Math.floor((value - day * 86400) / 3600);
    const min = Math.floor((value - day * 86400 - hour * 3600) / 60);
    const sec = Math.floor(value - day * 86400 - hour * 3600 - min * 60);
    return hour > 0 ? { hour, min, sec } : min > 0 ? { min, sec } : { sec };
  }
};

/**
 * function to convert seconds to time string (00:00:00)
 * @param {number} value time to be converted (in seconds)
 * @param {boolean} seconds whether the date should include seconds
 * @returns {string} an object containing the count of days, hours, minutes and seconds as string
 */

export const toTimeString = (value: number, seconds: boolean = true): string => {
  const response = time(value);
  if (isObject(response)) {
    const length = Object.keys(response).length;
    // const mapped = Object.values(response)
    //   .map((item) => pad(item))
    //   .join(":");
    // return length === 1 ? "00:" + mapped : length === 3 && !seconds ? mapped.slice(0, 5) : mapped;
    const mapped = ["hour", "min", "sec"].map((unit) => pad(response[unit] ?? 0)).join(":");
    return length === 3 && !seconds ? mapped.slice(0, 5) : mapped;
  }
  return "00:00:00";
};

/**
 * function to convert 24-hour time string to AM/PM format
 * @param {number} value time to be converted in seconds
 * @param {boolean} seconds whether the date should include seconds
 * @returns {string} the time in AM/PM format
 */

export const toTimeAmPmString = (value: number, seconds: boolean = true, am: string = "am", pm: string = "pm"): string => {
  const timeString = toTimeString(value, seconds);
  const time = timeString.split(":");
  const hour = Number(time.shift());
  if (hour >= 12) {
    const diff = 12 - Number(hour);
    return pad(Math.abs(diff === 0 ? 12 : diff)) + ":" + time.map((item) => pad(item)).join(":") + " " + pm;
  }
  return timeString + " " + am;
};

/**
 * function to add leading zeros
 * @param {number} value number to be filled with leading zeros
 * @param {number} size result length (default 2)
 * @returns {string} value if the leading zeros
 */

export const pad = (value: number | any, size: number = 2): string => {
  return !isNaN(Number.parseFloat(value)) ? "0".repeat(Math.max(0, size - Math.abs(value).toString().length)) + Math.abs(value) : "";
};

/**
 * function to convert date to ISO string with timezone
 * @param {Date} date the date to be converted
 * @param {string} time the time to be set
 * @returns {string} date as ISO string
 */

export const toISOString = (date: Date | null, time?: string | null, timeZone: boolean = false): string => {
  if (!date) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  // const dateISO = date.toISOString();
  // const dateString = dateISO.split("T")[0];
  const dateString = `${year}-${month}-${day}`;
  const newTime = hasValue(time) ? time : toTimeString(toSeconds(date));
  return timeZone ? `${dateString}T${newTime}${getTimeZoneOffset(date)}` : `${dateString}T${newTime}Z`;
};

/**
 * function to get convert a given date to the database offset format
 * @param {Date} date the date to be converted
 * @returns {string} the offset format YYYY-MM-DD HH:MM:SS 00:00
 */

export const toOffsetString = (date: Date | null): string => {
  if (!date) return "";
  const dateISO = toISOString(date);
  const dateTime = dateISO.split("T");
  const time = dateTime[1].substring(0, 8);
  const timeZone = getTimeZoneOffset(date);
  return `${dateTime[0]} ${time} ${timeZone}`;
};

/**
 * function to convert a given time as string to its equivalent in minutes
 * @param {string} value the time as string e.g. 10:00 AM or 14:00
 * @returns {number} the number of minutes e.g. 10:00 AM equals to 600
 */

export const toMinutes = (value: string | null, am: string = "AM"): number => {
  if (!value) return 0;
  const array = value.toUpperCase().split(" ");
  const time = array[0].split(":");
  const ampm = time.length === 2;
  const period = ampm ? array[1].toUpperCase() : null;
  const hour = Number(time[0]);
  const hours = hour * 60 + Number(time[1]);
  const extra = !ampm || period === am || hour === 12 ? 0 : 12 * 60;
  return hours + extra;
};

/**
 * function to convert a given time (from date) to its equivalent in seconds
 * @param {date} value the date to be converted
 * @returns {number} the time in seconds
 */

export const toSeconds = (value: Date): number => {
  if (!value) return 0;
  const hours = value.getHours();
  const minutes = value.getMinutes();
  const seconds = value.getSeconds();
  return hours * 3600 + minutes * 60 + seconds;
};

/**
 * function to return the start and end date of the appointment
 * @param {Date} date the date of the appointment
 * @param {string} slot the time slot of the appointment
 * @param {number} duration the length of the slot
 * @param {string} timeZone the time zone offset
 * @returns {{ start: string, end: string }} the start and end date as ISO format YYYY-MM-DDTHH:MM:SSZ
 */

export const toDateStartEnd = (date: Date | null, slot: string | null, duration: number, timeZone: boolean = false): { start: string; end: string } => {
  if (!date) return { start: "", end: "" };
  const minutes = toMinutes(slot); // + date.getTimezoneOffset();
  const startTime = toTimeString(minutes * 60);
  const endTime = toTimeString((minutes + (duration ?? Constants.APPOINTMENT_DURATION)) * 60);
  return { start: toISOString(date, startTime, timeZone), end: toISOString(date, endTime, timeZone) };
};

/**
 * function to generate an array of a given size
 * @param {number} count size of the array
 * @returns {array} array of numbers
 */

export const generateArray = (count: number): Array<number> => {
  return Array.from(Array(count).keys());
};

/**
 * function to get the time offset of a given date (or current date if nothing is given)
 * @param {Date | null} date the date where the time offset should be retrieved
 * @returns {string} time offset
 */

export const getTimeZoneOffset = (date?: Date | null): string => {
  const offset = (date ?? new Date()).getTimezoneOffset();
  const behind = offset > 0;
  // return `${behind ? "-" : ""}${toTimeString(offset)}`;
  return `${behind ? "-" : ""}${toTimeString(offset * 60, false)}`;
};

/**
 * function to get the name of the timezone
 * @returns {string} the time zone name
 */

export const getTimeZoneDescription = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * function to add time zone offset to a given date
 * @param {Date} date date which the offset should be added
 * @returns {Date} the new date
 */

export const addTimeZoneOffset = (date?: Date | null): Date | null => {
  if (!date) return null;
  const timeZoneOffset = date.getTimezoneOffset();
  const minutes = timeZoneOffset < 0 ? -timeZoneOffset : timeZoneOffset;
  date.setMinutes(date.getMinutes() + minutes);
  return date;
};

/**
 * function to get the range of dates from a given date
 * @param {Date} start the starting date
 * @param {number} length the number of days
 * @returns {Array} the range [start, end]
 */

export const getDateRange = (start: Date = new Date(), length: number = 7): Array<any> => {
  const end = new Date(start);
  end.setDate(end.getDate() + length);
  // end.setDate(end.getDate() + (length - 1));
  // start.setHours(0, 0, 0);
  // end.setHours(23, 59, 59);
  return [toOffsetString(start).split(" ")[0], toOffsetString(end).split(" ")[0]];
};

/**
 * function to calculate the age based on the birthdate
 * @param {Date} date the birthdate
 * @returns {number} the age
 */

export const toAge = (date: Date | string): number => {
  const diff = Math.abs(Date.now() - new Date(date).getTime());
  return Math.floor(diff / (1000 * 3600 * 24) / 365.25);
};

/**
 * function to remove all non numeric characters from a string
 * @param {string} value the maked string
 * @returns {string} the new string with only numeric characters
 */

export const onlyNumber = (value: string): string => {
  return value.replace(/\D/g, "");
};

/**
 * function to remove all non alpha-numeric characters from a string
 * @param {string} value the maked string
 * @returns {string} the new string with only alpha-numeric characters
 */

export const onlyWord = (value: string): string => {
  return value.replace(/\W/g, "");
};

/**
 * function to remove all properties that contains null or undefined values
 * @param {Object} obj the object to be modified
 * @returns {Object} the new object with all properties removed
 */

export const removeFromObject = (obj: any): Object => {
  Object.keys(obj).forEach((key: string) => {
    if (obj[key] === null) {
      delete obj[key];
    }
  });
  return obj;
};

/**
 * function to join a string that is separeted by a comma
 * @param {string} value the string to join
 * @returns {string} the new string without the comma
 */

export const replaceComma = (value: string): string => {
  return hasValue(value) ? value.replace(/,/, " ") : value;
};

const enterFullScreen = () => {
  const root: any = document.documentElement;

  if (root.requestFullscreen) {
    root.requestFullscreen();
  } else if (root.webkitRequestFullscreen) {
    root.webkitRequestFullscreen();
  } else if (root.mozRequestFullScreen) {
    root.mozRequestFullScreen();
  } else if (root.msRequestFullscreen) {
    root.msRequestFullscreen();
  }
};

const exitFullScreen = () => {
  const doc: any = document;

  if (doc.exitFullscreen) {
    doc.exitFullscreen();
  } else if (doc.webkitExitFullscreen) {
    doc.webkitExitFullscreen();
  } else if (doc.mozCancelFullScreen) {
    doc.mozCancelFullScreen();
  } else if (doc.msExitFullscreen) {
    doc.msExitFullscreen();
  }
};

const isFullScreen = () => {
  const doc: any = document;

  return !!(doc.fullscreenElement || doc.mozFullScreenElement || doc.webkitFullscreenElement || doc.msFullscreenElement);
};

export const toggleFullScreen = () => (isFullScreen() ? exitFullScreen() : enterFullScreen());

export const download = (documentData: Buffer, documentName: string, a: HTMLAnchorElement) => {
  const url = URL.createObjectURL(new Blob([new Uint8Array(documentData)], { type: "application/octet-stream" }));

  a.href = url;
  a.download = documentName;
  a.click();

  URL.revokeObjectURL(url);
};

export const hexToRGB = (hex: string) => {
  hex = hex.replace(/^#/, "");
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return { r, g, b };
};
